import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { Logo, Wordmark } from '@/components/ui/Logo';
import { useCreateWedding, useMe } from '@/features/shell/queries';

export function CreateWeddingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const createWedding = useCreateWedding();

  const [title, setTitle] = useState('');
  const [partnerOneName, setPartnerOne] = useState('');
  const [partnerTwoName, setPartnerTwo] = useState('');
  const [date, setDate] = useState('');
  const [city, setCity] = useState('');

  // Someone who already has a wedding does not belong on this page.
  if ((me.data?.weddings.length ?? 0) > 0) {
    return <Navigate to={`/app/w/${me.data?.weddings[0]?.wedId}/home`} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const wedding = await createWedding.mutateAsync({
      title,
      ...(partnerOneName ? { partnerOneName } : {}),
      ...(partnerTwoName ? { partnerTwoName } : {}),
      ...(date ? { date } : {}),
      ...(city ? { city } : {}),
      ...(me.data?.workspaces[0] ? { wsId: me.data.workspaces[0].wsId } : {}),
    });
    navigate(`/app/w/${wedding.wedId}/home`, { replace: true });
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-lg flex-col justify-center px-4 py-12">
      <div className="mb-8 flex items-center gap-3">
        <Logo size={40} tone="leaf" />
        <Wordmark className="text-leaf" />
      </div>

      <h1 className="font-display text-display">{t('onboarding.title')}</h1>
      <p className="mt-2 text-body text-leaf-soft">{t('onboarding.body')}</p>

      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-4">
        <Field label={t('onboarding.weddingTitle')} hint={t('onboarding.weddingTitleHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('onboarding.partnerOne')}>
            {({ id }) => (
              <TextInput
                id={id}
                value={partnerOneName}
                onChange={(e) => setPartnerOne(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('onboarding.partnerTwo')}>
            {({ id }) => (
              <TextInput
                id={id}
                value={partnerTwoName}
                onChange={(e) => setPartnerTwo(e.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('onboarding.date')} hint={t('onboarding.dateHint')}>
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('onboarding.city')}>
            {({ id }) => (
              <TextInput id={id} value={city} onChange={(e) => setCity(e.target.value)} />
            )}
          </Field>
        </div>

        {createWedding.isError && (
          <p role="alert" className="text-body text-status-declinedFg">
            {t('common.somethingWrong')}
          </p>
        )}

        <Button type="submit" loading={createWedding.isPending}>
          {createWedding.isPending ? t('onboarding.creating') : t('onboarding.create')}
        </Button>
      </form>
    </main>
  );
}
